import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Circle } from '@phosphor-icons/react';

import {
  CaretRightStyled,
  ListItemButtonStyled,
  ListItemIconStyled,
  ListItemStyled,
  ListItemTextStyled,
  MessageStyled,
  TitleStyled,
  DaysMessageStyled,
} from './InboxItemStyled';
import { RouterLink, Spacer } from 'theSource/components';
import { FeedMessage } from 'theSource/types';
import { selectViewedIDs } from 'theSource/features/Inbox/services';
import { unreadMessageBadgeColor } from 'theSource/theme';

export interface InboxItemProps
  extends Pick<FeedMessage, 'id' | 'body' | 'title' | 'read'> {
  daysAgo: number;
}

export const InboxItem = memo(
  ({ body, daysAgo, id, title, read }: InboxItemProps) => {
    const { t } = useTranslation('pages.inbox');
    const viewedIDs = useSelector(selectViewedIDs);
    const [messageRead, setMessageRead] = useState(read);

    useEffect(() => {
      if (viewedIDs.includes(id)) {
        setMessageRead(true);
      }
    }, [viewedIDs, id]);

    return (
      <ListItemStyled data-testid="inbox-item">
        <ListItemButtonStyled component={RouterLink} href={id.toString()}>
          {messageRead ? (
            <Spacer width={3} data-testid="unread-spacer" />
          ) : (
            <Circle
              size={12}
              color={unreadMessageBadgeColor}
              weight="fill"
              data-testid="unread-indicator"
            />
          )}
          <Spacer width={2} />
          <ListItemTextStyled>
            <TitleStyled variant="body2Heading">{title}</TitleStyled>
            <Spacer height={1} />
            <MessageStyled
              variant="caption"
              dangerouslySetInnerHTML={{ __html: body }}
            />
            <Spacer height={2} />
            <DaysMessageStyled variant="overline">
              {daysAgo === 1 ? t('dayAgo') : t('daysAgo', { days: daysAgo })}
            </DaysMessageStyled>
          </ListItemTextStyled>
          <ListItemIconStyled>
            <CaretRightStyled weight="bold" data-testid="caret-right-icon" />
          </ListItemIconStyled>
        </ListItemButtonStyled>
      </ListItemStyled>
    );
  }
);
